import { Validators } from '@biteinc/common';
import { AlertType, allEnumValues, CanaryTier, CardSchemeId, CountryCode, CouponCodeTransform, CouponProvider, FulfillmentMethod, IntegrationSystem, LocationLockOwner, LocationModeDeprecated, LocationMultiPaymentSupport, LocationMultiVendorSupport, LocationPaymentType, LocationPresetTipType, LocationState, LocationStateHelper, LocationTipsLevel, OrderChannel, OrderChannelHelper, PromotionThresholdType, ThirdPartyOrderProvider, Timezone, UserRight, WayfinderHotspotType, WayfinderHotspotVariant, WayfinderLinkType, Weekday, } from '@biteinc/enums';
import { AbstractModelSchema } from './abstract_model_schema.js';
const openHoursScheduleSchema = {
    type: 'object',
    fields: {
        from: {
            type: 'int',
        },
        to: {
            type: 'int',
        },
        openingHoursString: {
            type: 'keyString',
        },
    },
};
export const locationSchema = {
    displayName: 'location',
    type: 'object',
    fields: {
        ...AbstractModelSchema.fields,
        activeFulfillmentMethods: {
            type: 'array',
            elementType: 'int',
            elementValues: allEnumValues(FulfillmentMethod),
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
        },
        assignedFulfillmentMethods: {
            type: 'array',
            elementType: 'int',
            tabId: 'bite-settings',
            elementValues: allEnumValues(FulfillmentMethod),
            isHidden: true,
            isReadOnly: true,
        },
        coordinates: {
            type: 'array',
            elementType: 'float',
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
            minCount: 2,
            maxCount: 2,
        },
        embedId: {
            type: 'keyString',
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
        },
        integrationById: {
            type: 'map',
            key: {
                type: 'mongoId',
            },
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
            fields: {
                _id: {
                    type: 'mongoId',
                },
                system: {
                    type: 'int',
                    values: allEnumValues(IntegrationSystem),
                },
                syncedData: {
                    type: 'object',
                    isFreeform: true,
                },
            },
        },
        menuId: {
            type: 'mongoId',
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
        },
        lockExpiresAt: {
            type: 'timestamp',
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
        },
        indefiniteLockOwner: {
            type: 'stringEnum',
            enum: LocationLockOwner,
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
        },
        mode: {
            type: 'int',
            values: allEnumValues(LocationModeDeprecated),
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
        },
        name: {
            type: 'keyString',
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
        },
        normalizedName: {
            type: 'keyString',
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
        },
        openingHoursByFulfillmentMethod: {
            type: 'array',
            elementType: 'object',
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
            fields: {
                fulfillmentMethod: {
                    type: 'int',
                    values: allEnumValues(FulfillmentMethod),
                },
                [Weekday.Mon]: openHoursScheduleSchema,
                [Weekday.Tue]: openHoursScheduleSchema,
                [Weekday.Wed]: openHoursScheduleSchema,
                [Weekday.Thu]: openHoursScheduleSchema,
                [Weekday.Fri]: openHoursScheduleSchema,
                [Weekday.Sat]: openHoursScheduleSchema,
                [Weekday.Sun]: openHoursScheduleSchema,
            },
        },
        openingHoursByFulfillmentMethodWithOverrides: {
            type: 'array',
            elementType: 'object',
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
            fields: {
                fulfillmentMethod: {
                    type: 'int',
                    values: allEnumValues(FulfillmentMethod),
                },
                [Weekday.Mon]: {
                    type: 'array',
                    elementType: 'object',
                    fields: openHoursScheduleSchema.fields,
                },
                [Weekday.Tue]: {
                    type: 'array',
                    elementType: 'object',
                    fields: openHoursScheduleSchema.fields,
                },
                [Weekday.Wed]: {
                    type: 'array',
                    elementType: 'object',
                    fields: openHoursScheduleSchema.fields,
                },
                [Weekday.Thu]: {
                    type: 'array',
                    elementType: 'object',
                    fields: openHoursScheduleSchema.fields,
                },
                [Weekday.Fri]: {
                    type: 'array',
                    elementType: 'object',
                    fields: openHoursScheduleSchema.fields,
                },
                [Weekday.Sat]: {
                    type: 'array',
                    elementType: 'object',
                    fields: openHoursScheduleSchema.fields,
                },
                [Weekday.Sun]: {
                    type: 'array',
                    elementType: 'object',
                    fields: openHoursScheduleSchema.fields,
                },
            },
        },
        thirdPartyOrdering: {
            type: 'array',
            elementType: 'object',
            tabId: 'bite-settings',
            biteOnly: true,
            displayName: 'Third Party Ordering',
            ui: 'accordion',
            uiSubtype: 'ThirdPartyOrdering',
            fields: {
                provider: {
                    type: 'stringEnum',
                    displayName: 'Provider',
                    enum: ThirdPartyOrderProvider,
                    isHidden: true,
                    required: true,
                },
                restaurantId: {
                    type: 'keyString',
                    displayName: 'Restaurant ID',
                    tooltip: {
                        text: 'The Paytronix store/restaurant ID for this site.',
                    },
                    required: true,
                    conditions: [
                        {
                            field: 'provider',
                            values: [ThirdPartyOrderProvider.PaytronixOnlineOrderingWebhook],
                        },
                    ],
                },
                notificationsEnabled: {
                    type: 'bool',
                    displayName: 'Send Order Notifications',
                    defaultValue: false,
                    required: true,
                    tooltip: {
                        text: 'If checked, third-party orders will trigger notifications following the same rules as first-party orders.',
                    },
                },
                lastOrderReceivedAt: {
                    type: 'timestamp',
                    displayName: 'Last Order Received At',
                    isReadOnly: true,
                },
            },
        },
        orgDomain: {
            type: 'keyString',
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
        },
        orgId: {
            type: 'mongoId',
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
        },
        orgName: {
            type: 'keyString',
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
        },
        orgNormalizedName: {
            type: 'keyString',
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
        },
        shortCode: {
            type: 'keyString',
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
        },
        urlSlug: {
            type: 'keyString',
            tabId: 'bite-settings',
            validationRegex: '^[a-z0-9-_]{4,64}$',
            displayName: 'Url Slug for Flash / Web',
            tooltip: {
                text: 'Lowercase alphanumeric, underscores, and hyphens. 4-64 characters.',
            },
            orConditions: [
                {
                    field: 'orderChannel',
                    values: [OrderChannel.Web, OrderChannel.Catering],
                },
                {
                    field: 'flashEnabled',
                    values: [true],
                },
            ],
        },
        siteId: {
            type: 'mongoId',
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
        },
        useOrdersApiV2Flash: {
            type: 'bool',
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
        },
        orderChannel: {
            type: 'stringEnum',
            ui: 'dropdown',
            isReadOnly: true,
            displayName: 'Order Channel',
            tabId: 'bite-settings',
            tooltip: {
                text: 'This value cannot be changed once a location is created.',
            },
            enum: OrderChannel,
            enumNameHelper: OrderChannelHelper.name,
        },
        channelName: {
            type: 'shortString',
            displayName: 'Channel Name',
            required: true,
            tabId: 'bite-settings',
        },
        channelNormalizedName: {
            type: 'keyString',
            tabId: 'bite-settings',
            isHidden: true,
            isReadOnly: true,
        },
        state: {
            type: 'int',
            values: allEnumValues(LocationState),
            enum: LocationState,
            enumNameHelper: LocationStateHelper.name,
            ui: 'dropdown',
            required: true,
            biteOnly: true,
            tabId: 'bite-settings',
            tooltip: {
                text: 'A channel is Bite-Only if and only if the site is Bite-Only.',
            },
        },
        wentLiveAt: {
            type: 'timestamp',
            displayName: 'Went Live At',
            tabId: 'bite-settings',
            required: true,
            isNullable: true,
            writeRight: UserRight.BiteOps,
        },
        menuReferenceLocationId: {
            displayName: 'Menu Reference Location',
            type: 'mongoId',
            isNullable: true,
            biteOnly: true,
            ui: 'dropdown',
            tabId: 'bite-settings',
        },
        multiVendorSupport: {
            type: 'int',
            values: allEnumValues(LocationMultiVendorSupport),
            enum: LocationMultiVendorSupport,
            ui: 'dropdown',
            displayName: 'Multi-Vendor Support',
            biteOnly: true,
            tabId: 'bite-settings',
        },
        multiPaymentSupport: {
            type: 'stringEnum',
            enum: LocationMultiPaymentSupport,
            ui: 'dropdown',
            displayName: 'Multiple Payment Integrations Support',
            tooltip: {
                text: 'This will only be utilized if the location is using stripe as the payment integration.',
            },
            biteOnly: true,
            tabId: 'bite-settings',
            conditions: [
                {
                    field: 'multiVendorSupport',
                    values: [LocationMultiVendorSupport.Enabled],
                },
            ],
        },
        countryCode: {
            type: 'stringEnum',
            enum: CountryCode,
            required: true,
            isReadOnly: true,
            displayName: 'Country',
            ui: 'dropdown',
            tabId: 'bite-settings',
            tooltip: {
                text: 'This value is set at the site level.',
            },
        },
        timezone: {
            type: 'stringEnum',
            enum: Timezone,
            required: true,
            biteOnly: true,
            isPermanent: true,
            displayName: 'Time Zone',
            ui: 'dropdown',
            tabId: 'bite-settings',
            tooltip: {
                text: 'This value is set at the site level.',
            },
        },
        googlePlace: {
            type: 'object',
            displayName: 'Google Place',
            tabId: 'bite-settings',
            biteOnly: true,
            required: true,
            isNullable: true,
            tooltip: {
                text: 'If this config is removed, then we will not sync open hours from Google.',
            },
            fields: {
                id: {
                    type: 'keyString',
                    required: true,
                    displayName: 'ID',
                    tooltip: {
                        text: "This value is fetched from Google using the site's address. If the fetch fails, it can be provided manually.",
                    },
                },
                name: {
                    type: 'shortString',
                    required: true,
                    isReadOnly: true,
                    displayName: 'Name',
                },
                address: {
                    type: 'shortString',
                    required: true,
                    isReadOnly: true,
                    displayName: 'Address',
                },
                url: {
                    type: 'keyString',
                    required: true,
                    isReadOnly: true,
                    displayName: 'URL',
                },
                coordinates: {
                    type: 'array',
                    elementType: 'float',
                    required: true,
                    isHidden: true,
                    isReadOnly: true,
                    minCount: 2,
                    maxCount: 2,
                },
            },
        },
        // 2026-01-21: Hiding this for now since our own implementation of delphi is gone, but we will
        // probably re-introduce this feature with Chase biometrics in the future.
        usesDelphi: {
            type: 'bool',
            displayName: 'Uses Delphi',
            tabId: 'kiosk-settings',
            biteOnly: true,
            isHidden: true,
            conditions: [
                {
                    field: 'orderChannel',
                    values: [OrderChannel.Kiosk],
                },
            ],
        },
        orderLeadTimeRules: {
            displayName: 'Order Lead Time Rules',
            type: 'array',
            elementType: 'object',
            elementSubtype: 'OrderLeadTimeRule',
            ui: 'accordion',
            uiSubtype: 'OrderLeadTimeRule',
            tabId: 'order-lead-time',
            writeRight: UserRight.ManageChannelSettings,
            tooltip: {
                text: 'Each rule counts the items in the order that belong to its menu sections, then applies ' +
                    'the shortest time window whose max item count fits that total. Each threshold is the max ' +
                    'number of items the kitchen can prepare within that time. If the count exceeds every ' +
                    'threshold, the largest configured time applies. When multiple rules match an order, the ' +
                    'longest lead time wins.',
            },
        },
        kioskDiningOptions: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'DiningOption',
            displayName: 'Dining Options (Kiosk Only)',
            tabId: 'kiosk-settings',
            ui: 'accordion',
            uiSubtype: 'DiningOptions',
            writeRight: UserRight.ManageChannelSettings,
        },
        flashDiningOptions: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'DiningOption',
            required: true,
            displayName: 'Dining Options (Web Only)',
            tabId: 'flash-settings',
            ui: 'accordion',
            uiSubtype: 'DiningOptions',
            writeRight: UserRight.ManageChannelSettings,
            conditions: [
                {
                    field: 'orderChannel',
                    values: allEnumValues(OrderChannel).filter((orderChannel) => {
                        return OrderChannelHelper.isFlash(orderChannel);
                    }),
                },
            ],
        },
        flashPhoto: {
            displayName: 'Photo',
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 1,
            keepAspectRatio: 'contain',
            size: {
                width: 640,
                height: 360,
            },
            tabId: 'flash-settings',
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: 'Shown on the card for this location on the org locations screen.',
            },
            conditions: [
                {
                    field: 'orderChannel',
                    values: allEnumValues(OrderChannel).filter((orderChannel) => {
                        return OrderChannelHelper.isOrderAhead(orderChannel);
                    }),
                },
            ],
        },
        flashDescription: {
            type: 'longString',
            displayName: 'Description',
            tabId: 'flash-settings',
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: 'A short description shown under the location name on the org locations screen.',
            },
            conditions: [
                {
                    field: 'orderChannel',
                    values: allEnumValues(OrderChannel).filter((orderChannel) => {
                        return OrderChannelHelper.isOrderAhead(orderChannel);
                    }),
                },
            ],
        },
        paymentType: {
            type: 'int',
            values: allEnumValues(LocationPaymentType),
            displayName: 'Payment Type',
            ui: 'dropdown',
            tabId: 'kiosk-settings',
            biteOnly: true,
        },
        soundAlerts: {
            type: 'object',
            displayName: 'Sound Alerts',
            tabId: 'kiosk-settings',
            biteOnly: true,
            fields: {
                'transaction-start': {
                    type: 'object',
                    displayName: 'Transaction Start',
                    fields: {
                        url: {
                            type: 'keyString',
                            displayName: 'Sound URL',
                        },
                        volume: {
                            type: 'float',
                            displayName: 'Volume (0-1)',
                        },
                        repetitions: {
                            type: 'int',
                            displayName: 'Repetitions',
                        },
                    },
                },
                'transaction-end': {
                    type: 'object',
                    displayName: 'Transaction End',
                    fields: {
                        url: {
                            type: 'keyString',
                            displayName: 'Sound URL',
                        },
                        volume: {
                            type: 'float',
                            displayName: 'Volume (0-1)',
                        },
                        repetitions: {
                            type: 'int',
                            displayName: 'Repetitions',
                        },
                    },
                },
                'clerk-attention': {
                    type: 'object',
                    displayName: 'Clerk Attention',
                    fields: {
                        url: {
                            type: 'keyString',
                            displayName: 'Sound URL',
                        },
                        volume: {
                            type: 'float',
                            displayName: 'Volume (0-1)',
                        },
                        repetitions: {
                            type: 'int',
                            displayName: 'Repetitions',
                        },
                    },
                },
                error: {
                    type: 'object',
                    displayName: 'Error',
                    fields: {
                        url: {
                            type: 'keyString',
                            displayName: 'Sound URL',
                        },
                        volume: {
                            type: 'float',
                            displayName: 'Volume (0-1)',
                        },
                        repetitions: {
                            type: 'int',
                            displayName: 'Repetitions',
                        },
                    },
                },
            },
        },
        allowPayAtCashier: {
            type: 'bool',
            displayName: 'Allow paying at the cashier with cash',
            tabId: 'kiosk-settings',
            biteOnly: true,
            conditions: [
                {
                    field: 'paymentType',
                    values: [LocationPaymentType.CreditCard],
                },
            ],
        },
        tipsLevel: {
            enum: LocationTipsLevel,
            type: 'int',
            values: allEnumValues(LocationTipsLevel),
            writeRight: UserRight.ManageCommonSettings,
            displayName: 'Tipping',
            ui: 'dropdown',
            tabId: 'common-settings',
            tooltip: {
                text: 'Enabled only if the POS integration supports tips.',
            },
        },
        defaultTip: {
            type: 'object',
            tabId: 'common-settings',
            writeRight: UserRight.ManageCommonSettings,
            displayName: 'Default Tip',
            isNullable: true,
            fields: {
                amount: {
                    type: 'float',
                    min: 0.01,
                    required: true,
                    defaultValue: 0,
                },
                type: {
                    type: 'stringEnum',
                    enum: LocationPresetTipType,
                    ui: 'dropdown',
                    defaultValue: LocationPresetTipType.Percentage,
                    required: true,
                    tooltip: {
                        text: 'If a fixed default tip is greater than the subtotal of the order, then the default tip will not be applied.',
                    },
                },
            },
        },
        presetTips: {
            type: 'array',
            elementType: 'object',
            fields: {
                amount: {
                    type: 'float',
                    min: 0.01,
                    required: true,
                },
                type: {
                    type: 'stringEnum',
                    enum: LocationPresetTipType,
                    ui: 'dropdown',
                    required: true,
                    tooltip: {
                        text: 'If a fixed preset tip is greater than the subtotal of the order, then the preset tip will not be presented to the guest.',
                    },
                },
            },
            minCount: 2,
            maxCount: 3,
            displayName: 'Preset Tips',
            ui: 'accordion',
            uiSubtype: 'PresetTips',
            tabId: 'common-settings',
            placeholder: 'Preset Tip Amounts',
            conditions: [
                {
                    field: 'tipsLevel',
                    values: [LocationTipsLevel.Forced, LocationTipsLevel.On],
                },
            ],
            writeRight: UserRight.ManageChannelSettings,
        },
        couponProvider: {
            displayName: 'Coupon Provider',
            type: 'stringEnum',
            enum: CouponProvider,
            tabId: 'common-settings',
            ui: 'dropdown',
            writeRight: UserRight.ManageCommonSettings,
        },
        couponsHaveBarcode: {
            type: 'bool',
            displayName: 'Coupons have barcode',
            tabId: 'common-settings',
            tooltip: {
                text: `Unset this if coupon doesn't have barcode, this hides the coupon scan instruction at Order Summary.`,
            },
            conditions: [
                {
                    field: 'couponProvider',
                    values: allEnumValues(CouponProvider).filter((couponProvider) => {
                        return couponProvider !== CouponProvider.None;
                    }),
                },
            ],
            writeRight: UserRight.ManageChannelSettings,
        },
        couponCodeTransform: {
            displayName: 'Transform Coupon Codes',
            type: 'stringEnum',
            enum: CouponCodeTransform,
            ui: 'dropdown',
            tabId: 'common-settings',
            conditions: [
                {
                    field: 'couponProvider',
                    values: [CouponProvider.Loyalty, CouponProvider.Pos],
                },
            ],
            writeRight: UserRight.ManageCommonSettings,
            defaultValue: CouponCodeTransform.None,
        },
        minTransactionAmount: {
            type: 'price',
            displayName: 'Min Transaction Amount',
            tabId: 'common-settings',
            writeRight: UserRight.ManageChannelSettings,
        },
        maxTransactionAmount: {
            type: 'price',
            displayName: 'Max Transaction Amount',
            tabId: 'common-settings',
            writeRight: UserRight.ManageChannelSettings,
        },
        maxAlcoholicItemCountPerOrder: {
            type: 'int',
            min: 0,
            tabId: 'common-settings',
            displayName: 'Max alcoholic beverage items per order',
            writeRight: UserRight.ManageCommonSettings,
            tooltip: {
                text: 'Max alcoholic beverage items allowed per order.',
            },
        },
        maxSpecialItemCountPerOrder: {
            type: 'int',
            min: 0,
            tabId: 'common-settings',
            displayName: 'Max Special Offer Item Per Order',
            writeRight: UserRight.ManageCommonSettings,
            tooltip: {
                text: 'Max count of items with category "Special Offer" allowed per order. Set 0 for no limit on special offer items per order.',
            },
        },
        secondsToTimeoutOnValidateOrder: {
            type: 'int',
            displayName: 'Seconds Until Timeout On Validate Order',
            tabId: 'common-settings',
            writeRight: UserRight.ManageCommonSettings,
            tooltip: {
                text: 'Default is 30s. Can be increased for locations with ASAP orders that have slow network traffic.',
            },
            isNullable: true,
        },
        secondsToTimeoutOnSendOrder: {
            type: 'int',
            displayName: 'Seconds Until Timeout On Send Order',
            tabId: 'common-settings',
            writeRight: UserRight.ManageCommonSettings,
            tooltip: {
                text: 'Default is 80s for Brink, 60s for Infor and Olo, otherwise 30s. Can be increased for locations with ASAP orders that have slow network traffic.',
            },
            isNullable: true,
        },
        maxSendOrderAttempts: {
            type: 'int',
            displayName: 'Max Send Order Attempts',
            tabId: 'common-settings',
            writeRight: UserRight.ManageCommonSettings,
            required: true,
            // have a low number of attempts because if it doesn't work the first time, it probably won't
            // work the rest also it means we can fail faster for better UX
            defaultValue: 2,
            min: 1,
            max: 3,
            tooltip: {
                text: 'Minimum of 1 and maximum of 3',
            },
        },
        paymentTerminalIdleText: {
            type: 'codeString',
            displayName: 'Payment Terminal Idle Text',
            tabId: 'kiosk-settings',
            tooltip: {
                text: 'Ingenico: 23 chars max in length, 5 lines. DO NOT CENTER.',
            },
            writeRight: UserRight.ManageChannelSettings,
        },
        notes: {
            type: 'longString',
            biteOnly: true,
            tabId: 'bite-settings',
            tooltip: {
                text: 'Any important information that should be kept for us to see.',
            },
        },
        contactInfo: {
            displayName: 'Contact Info',
            type: 'longString',
            biteOnly: true,
            tabId: 'bite-settings',
            tooltip: {
                text: 'For storing contact info like manager name, phone number, address.',
            },
        },
        flashEnabled: {
            type: 'bool',
            displayName: 'Flash Enabled',
            biteOnly: true,
            tabId: 'bite-settings',
            conditions: [
                {
                    field: 'orderChannel',
                    values: [OrderChannel.Kiosk],
                },
            ],
            tooltip: {
                text: 'Please! Why are you enabling this? We should have a dedicated web channel instead!',
                isWarning: true,
            },
            // We want to discourage turning on this flag, but leaving this open to eng just in case
            writeRight: UserRight.BiteEng,
        },
        smsOrderStatusTransmittingNotification: {
            type: 'keyString',
            displayName: 'SMS Order Transmitting Notification',
            tabId: 'sms-settings',
            tooltip: {
                text: `
          Only available for select POS systems.
          The SMS notification to send to the guest when their order is being sent to the kitchen.
          If blank, no notification will be sent.`,
            },
            writeRight: UserRight.ManageChannelSettings,
        },
        smsOrderStatusOnHoldNotification: {
            type: 'keyString',
            displayName: 'SMS Order On Hold Notification',
            tabId: 'sms-settings',
            tooltip: {
                text: `
          Only available for select POS systems.
          The SMS notification to send to the guest when their order has been placed on hold.
          If blank, no notification will be sent.`,
            },
            writeRight: UserRight.ManageChannelSettings,
        },
        smsOrderStatusInProgressNotification: {
            type: 'keyString',
            displayName: 'SMS Order In Progress Notification',
            tabId: 'sms-settings',
            tooltip: {
                text: `
          Only available for select POS systems.
          The SMS notification to send to the guest when the kitchen starts working on their order.
          If blank, no notification will be sent.`,
            },
            writeRight: UserRight.ManageChannelSettings,
        },
        smsOrderStatusCompleteNotification: {
            type: 'keyString',
            displayName: 'SMS Order Completed Notification',
            tabId: 'sms-settings',
            tooltip: {
                text: `
            Only available for select POS systems.
            The SMS notification to send to the guest when their order has been completed." +
            If blank, no notification will be sent.`,
            },
            writeRight: UserRight.ManageChannelSettings,
        },
        smsOrderStatusCancelledNotification: {
            type: 'keyString',
            displayName: 'SMS Order Cancelled by POS Notification',
            tabId: 'sms-settings',
            tooltip: {
                text: `
          Only available for select POS systems.
          The SMS notification to send to the guest when their order has been cancelled by the POS.
          If blank, no notification will be sent.`,
            },
            writeRight: UserRight.ManageChannelSettings,
        },
        smsOrderStatusUnknownNotification: {
            type: 'keyString',
            displayName: 'SMS Order Status Unknown Notification',
            tabId: 'sms-settings',
            tooltip: {
                text: `
          Only available for select POS systems.
          The SMS notification to send to the guest when their order's status is unknown.
          This could occur when the status cannot be retrieved or the order takes too long to complete.
          If blank, no notification will be sent.`,
            },
            writeRight: UserRight.ManageChannelSettings,
        },
        smsNotificationOrderHas86dContentsMessageTemplate: {
            type: 'keyString',
            displayName: 'SMS Order Has 86d Contents Message',
            tabId: 'sms-settings',
            tooltip: {
                text: 'Message to send to guests when order has an item 86d.',
            },
            writeRight: UserRight.ManageChannelSettings,
        },
        futureOrdersSmsNotificationOrderHas86dContentsMessageTemplate: {
            type: 'keyString',
            displayName: 'Future Orders SMS Order Has 86d Contents Message',
            tabId: 'sms-settings',
            tooltip: {
                text: 'Message to send to guests when order has an item 86d. For Bite Future Orders.',
            },
            writeRight: UserRight.ManageChannelSettings,
        },
        emailNotificationOrderHas86dContentsMessageTemplate: {
            type: 'keyString',
            displayName: 'Email Order Has 86d Contents Message',
            tabId: 'sms-settings',
            tooltip: {
                text: 'Message to send to guests when order has an item 86d.',
            },
            writeRight: UserRight.ManageChannelSettings,
        },
        futureOrdersEmailNotificationOrderHas86dContentsMessageTemplate: {
            type: 'keyString',
            displayName: 'Future Orders Email Order Has 86d Contents Message',
            tabId: 'sms-settings',
            tooltip: {
                text: 'Message to send to guests when order has an item 86d. For Bite Future Orders.',
            },
            writeRight: UserRight.ManageChannelSettings,
        },
        emailNotificationOrderFailedGenericMessageTemplate: {
            type: 'keyString',
            displayName: 'Email Order Failed Generic Message',
            tabId: 'sms-settings',
            tooltip: {
                text: 'Generic message to send to guests when order has failed to send. For Bite Future Orders.',
            },
            writeRight: UserRight.ManageChannelSettings,
        },
        smsNotificationOrderFailedGenericMessageTemplate: {
            type: 'keyString',
            displayName: 'SMS Order Failed Generic Message',
            tabId: 'sms-settings',
            tooltip: {
                text: 'Generic message to send to guests when order has failed to send. For Bite Future Orders.',
            },
            writeRight: UserRight.ManageChannelSettings,
        },
        futureOrdersEmailNotificationOrderFailedGenericMessageTemplate: {
            type: 'keyString',
            displayName: 'Future Orders Email Order Failed Generic Message',
            tabId: 'sms-settings',
            tooltip: {
                text: 'Generic message to send to guests when order has failed to send. For Bite Future Orders.',
            },
            writeRight: UserRight.ManageChannelSettings,
        },
        futureOrdersSmsNotificationOrderFailedGenericMessageTemplate: {
            type: 'keyString',
            displayName: 'Future Orders SMS Order Failed Generic Message',
            tabId: 'sms-settings',
            tooltip: {
                text: 'Generic message to send to guests when order has failed to send. For Bite Future Orders.',
            },
            writeRight: UserRight.ManageChannelSettings,
        },
        futureOrdersEmailNotificationOrderFailedTimeoutMessageTemplate: {
            type: 'keyString',
            displayName: 'Future Orders Email Order Failed Timeout Message',
            tabId: 'sms-settings',
            tooltip: {
                text: 'Timeout message to send to guests when order has failed to send. For Bite Future Orders.',
            },
            writeRight: UserRight.ManageChannelSettings,
        },
        futureOrdersSmsNotificationOrderFailedTimeoutMessageTemplate: {
            type: 'keyString',
            displayName: 'Future Orders SMS Order Failed Timeout Message',
            tabId: 'sms-settings',
            tooltip: {
                text: 'Timeout message to send to guests when order has failed to send. For Bite Future Orders.',
            },
            writeRight: UserRight.ManageChannelSettings,
        },
        futureOrdersSmsNotificationCancelledOrderEnabled: {
            type: 'bool',
            displayName: 'Enable guest SMS notification after order is cancelled',
            tabId: 'sms-settings',
            writeRight: UserRight.ManageChannelSettings,
        },
        futureOrdersSmsNotificationCancelledOrderMessageTemplate: {
            type: 'keyString',
            validationRegex: Validators.smsRegexString,
            displayName: 'SMS Message after order is cancelled',
            tabId: 'sms-settings',
            conditions: [
                {
                    field: 'futureOrdersSmsNotificationCancelledOrderEnabled',
                    values: [true],
                },
            ],
            writeRight: UserRight.ManageChannelSettings,
        },
        futureOrdersSmsNotificationRefundedOrderEnabled: {
            type: 'bool',
            displayName: 'Enable guest SMS notification after order is refunded',
            tabId: 'sms-settings',
            writeRight: UserRight.ManageChannelSettings,
        },
        futureOrdersSmsNotificationRefundedOrderMessageTemplate: {
            type: 'keyString',
            validationRegex: Validators.smsRegexString,
            displayName: 'SMS message after order is refunded',
            tabId: 'sms-settings',
            conditions: [
                {
                    field: 'futureOrdersSmsNotificationRefundedOrderEnabled',
                    values: [true],
                },
            ],
            writeRight: UserRight.ManageChannelSettings,
        },
        supportedCardSchemes: {
            type: 'array',
            elementType: 'int',
            elementValues: allEnumValues(CardSchemeId),
            displayName: 'Supported Card Schemes',
            ui: 'table',
            placeholder: 'Select accepted card schemes to display',
            tabId: 'flash-settings',
            writeRight: UserRight.ManageChannelSettings,
        },
        alertRules: {
            type: 'array',
            elementType: 'object',
            ui: 'accordion',
            uiSubtype: 'AlertRules',
            tabId: 'common-settings',
            biteOnly: true,
            tooltip: {
                text: 'Deprecated: Alert Rules are being replaced by KOR alerts. Set up kiosk and ordering alerts in KOR instead.',
                isWarning: true,
            },
            // Children carry no writeRight of their own so they inherit this one.
            fields: {
                email: {
                    type: 'email',
                    required: true,
                },
                frequencyByAlertType: {
                    type: 'map',
                    required: true,
                    displayName: 'Alert Subscriptions',
                    key: {
                        type: 'int',
                        values: allEnumValues(AlertType),
                        displayName: 'Alert Type',
                    },
                    fields: {
                        all: {
                            type: 'bool',
                        },
                        '15min': {
                            type: 'bool',
                            displayName: '@15m',
                        },
                        '60min': {
                            type: 'bool',
                            displayName: '@1h',
                        },
                    },
                },
            },
            // Deprecated in favor of KOR; locked to eng so no new rules get added.
            writeRight: UserRight.BiteEng,
        },
        freeItemPromotions: {
            type: 'array',
            elementType: 'object',
            ui: 'accordion',
            uiSubtype: 'FreeItemPromotions',
            tabId: 'common-settings',
            fields: {
                _id: {
                    type: 'mongoId',
                    isHidden: true,
                    isReadOnly: true,
                },
                name: {
                    type: 'keyString',
                },
                menuItemId: {
                    displayName: 'Menu Item',
                    type: 'mongoId',
                    ui: 'dropdown',
                },
                thresholdType: {
                    displayName: 'Threshold Type',
                    type: 'stringEnum',
                    enum: PromotionThresholdType,
                    ui: 'radiolist',
                    required: true,
                },
                thresholdValue: {
                    displayName: 'Threshold Value',
                    type: 'int',
                    required: true,
                    min: 1,
                },
                fulfillmentMethods: {
                    displayName: 'Apply to Dining Options',
                    type: 'array',
                    elementType: 'int',
                    elementValues: allEnumValues(FulfillmentMethod),
                    warningHtml: 'Each dining option can only have one active promotion running at any given time.',
                    ui: 'checklist',
                    required: true,
                    minCount: 1,
                },
            },
            writeRight: UserRight.ManageChannelSettings,
        },
        phoneNumber: {
            type: 'phone',
            tabId: 'common-settings',
            displayName: 'Phone Number',
            writeRight: UserRight.ManageChannelSettings,
            tooltip: {
                text: 'Please provide a 10-digit number without any spaces or brackets; no country code. (e.g. 4161234567)',
            },
        },
        fullAddress: {
            type: 'object',
            displayName: 'Full Civic Address',
            tabId: 'common-settings',
            writeRight: UserRight.ManageChannelSettings,
            subtype: 'Address',
        },
        useOrdersApiV2Kiosk: {
            type: 'bool',
            displayName: 'Uses Orders Api v2 for Kiosk',
            tabId: 'kiosk-settings',
            biteOnly: true,
        },
        useCacheBackedMenuFetcher: {
            type: 'bool',
            displayName: 'Use the cache-backed menu fetcher',
            biteOnly: true,
            tabId: 'bite-settings',
        },
        useGarconLogBatchUpload: {
            type: 'bool',
            displayName: 'Use Garcon log batch upload',
            writeRight: UserRight.BiteEng,
            biteOnly: true,
            tabId: 'bite-settings',
        },
        thirdPartyId: {
            type: 'keyString',
            displayName: 'Third Party Unique Identifier',
            validationRegex: '^[a-zA-Z0-9-_]{16,32}$',
            writeRight: UserRight.Admin,
            tabId: 'common-settings',
            isNullable: true,
            tooltip: {
                text: 'The unique identifier for the location in a third-party system. Alphanumeric, 16-32 characters.',
            },
        },
        syncAutomaticallyAndFetchLiveMenus: {
            type: 'bool',
            displayName: 'Include this location in fetch-sample-live-menus and staging quicksand',
            tabId: 'bite-settings',
            biteOnly: true,
        },
        syncPosWithHighPriority: {
            type: 'bool',
            displayName: 'Highest priority POS sync',
            biteOnly: true,
            tabId: 'bite-settings',
        },
        allowItemRecipients: {
            type: 'bool',
            isHidden: true,
            displayName: 'Allow Item Recipients',
            tooltip: {
                text: 'Only supported by Olo/Web',
            },
            tabId: 'common-settings',
            writeRight: UserRight.ManageChannelSettings,
        },
        includeRefundableUnsentOrdersInEmailToBite: {
            type: 'bool',
            displayName: 'Include Refundable Unsent Orders in Email to Bite',
            tabId: 'bite-settings',
            biteOnly: true,
            tooltip: {
                text: 'Orders that are unsent and refundable from this location will be included in the notification email sent to Accounts',
            },
        },
        useVitrine: {
            type: 'bool',
            displayName: 'Enable Vitrine',
            writeRight: UserRight.BiteSupport,
            tabId: 'kiosk-settings',
            biteOnly: true,
        },
        useKioskSetupWizard: {
            type: 'bool',
            displayName: 'Use Kiosk Setup Wizard',
            writeRight: UserRight.BiteSupport,
            tabId: 'kiosk-settings',
            biteOnly: true,
        },
        allowOfflineMode: {
            type: 'bool',
            displayName: 'Allow "Offline Mode"',
            writeRight: UserRight.BiteEng,
            tabId: 'bite-settings',
            biteOnly: true,
            tooltip: {
                text: 'If enabled, when a kiosk is disconnected from the Internet, the kiosk will shift into "Offline Mode" allowing it to function with limited features.',
            },
        },
        vitrineCanaryTier: {
            type: 'int',
            values: allEnumValues(CanaryTier),
            enum: CanaryTier,
            isNullable: true,
            displayName: 'Canary Tier (vitrine)',
            biteOnly: true,
            tabId: 'bite-settings',
            ui: 'dropdown',
        },
        gcnCanaryTier: {
            type: 'int',
            values: allEnumValues(CanaryTier),
            enum: CanaryTier,
            isNullable: true,
            displayName: 'Canary Tier (gcn)',
            biteOnly: true,
            tabId: 'bite-settings',
            ui: 'dropdown',
        },
        garconCanaryTier: {
            type: 'int',
            values: allEnumValues(CanaryTier),
            enum: CanaryTier,
            isNullable: true,
            displayName: 'Canary Tier (garçon)',
            biteOnly: true,
            tabId: 'bite-settings',
            ui: 'dropdown',
        },
        isLabLocation: {
            type: 'bool',
            displayName: 'Lab?',
            tabId: 'bite-settings',
            biteOnly: true,
        },
        isPilotLocation: {
            type: 'bool',
            displayName: 'Pilot?',
            tabId: 'bite-settings',
            biteOnly: true,
        },
        paymentSuccessImageVitrineOverride: {
            displayName: 'Payment Success Image',
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 1,
            keepAspectRatio: 'contain',
            size: {
                width: 952,
                height: 476,
            },
            exclusiveTo: 'vitrine',
            tabId: 'kiosk-settings',
            writeRight: UserRight.ManageMenu,
            canHaveLocalizationOverrides: true,
        },
        wayfinder: {
            type: 'array',
            elementType: 'object',
            tabId: 'kiosk-settings',
            biteOnly: true,
            displayName: 'Wayfinder',
            isNullable: true,
            ui: 'accordion',
            uiSubtype: 'Wayfinder',
            fields: {
                backgroundImage: {
                    type: 'array',
                    elementType: 'object',
                    elementSubtype: 'Image',
                    elementUniqueIdFieldName: 'origUrl',
                    size: {
                        width: 3840,
                        height: 1920,
                    },
                },
                floorIndex: {
                    type: 'int',
                    required: true,
                    min: 0,
                    max: 100,
                    displayName: 'Floor Index',
                    tooltip: {
                        text: 'The index of the floor in the wayfinder. The floor with the lowest index will be the default floor. The floor with the highest index will be the top floor.',
                    },
                },
                name: {
                    type: 'shortString',
                    required: true,
                    canHaveLocalizationOverrides: true,
                },
                hotspots: {
                    type: 'array',
                    elementType: 'object',
                    ui: 'accordion',
                    uiSubtype: 'WayfinderHotspots',
                    fields: {
                        position: {
                            type: 'object',
                            displayName: 'Position',
                            required: true,
                            fields: {
                                x: {
                                    type: 'int',
                                    required: true,
                                },
                                y: {
                                    type: 'int',
                                    required: true,
                                },
                            },
                        },
                        directions: {
                            type: 'shortString',
                            canHaveLocalizationOverrides: true,
                            tooltip: {
                                text: 'The directions on where the hotspot is located. This will be voiced when STORM navigation is enabled.',
                            },
                        },
                        type: {
                            type: 'stringEnum',
                            enum: WayfinderHotspotType,
                            ui: 'dropdown',
                            required: true,
                        },
                        image: {
                            type: 'array',
                            elementType: 'object',
                            elementSubtype: 'Image',
                            elementUniqueIdFieldName: 'origUrl',
                            canHaveLocalizationOverrides: true,
                            size: {
                                width: 275,
                                height: 200,
                            },
                        },
                        name: {
                            type: 'shortString',
                            canHaveLocalizationOverrides: true,
                            tooltip: {
                                text: 'The name of the hotspot. If the type is interactive, this will take precedence over the name of the linked entity.',
                            },
                        },
                        description: {
                            type: 'shortString',
                            canHaveLocalizationOverrides: true,
                            tooltip: {
                                text: 'The description of the hotspot. If the type is interactive, this will take precedence over the description of the linked entity.',
                            },
                        },
                        link: {
                            type: 'object',
                            fields: {
                                type: {
                                    type: 'stringEnum',
                                    enum: WayfinderLinkType,
                                    required: true,
                                },
                                targetId: {
                                    type: 'mongoId',
                                    displayName: 'Link Target ID',
                                    required: true,
                                    ui: 'dropdown',
                                    conditions: [
                                        {
                                            field: 'type',
                                            values: [
                                                WayfinderLinkType.Page,
                                                WayfinderLinkType.Section,
                                                WayfinderLinkType.Item,
                                            ],
                                        },
                                    ],
                                },
                            },
                            conditions: [
                                {
                                    field: 'type',
                                    values: [WayfinderHotspotType.Interactive],
                                },
                            ],
                        },
                        variant: {
                            type: 'stringEnum',
                            enum: WayfinderHotspotVariant,
                            ui: 'dropdown',
                            required: true,
                            conditions: [
                                {
                                    field: 'type',
                                    values: [WayfinderHotspotType.NonInteractive],
                                },
                            ],
                        },
                        kioskIds: {
                            type: 'array',
                            elementType: 'mongoId',
                            displayName: 'Kiosk IDs',
                            ui: 'table',
                            required: true,
                            conditions: [
                                {
                                    field: 'type',
                                    values: [WayfinderHotspotType.NonInteractive],
                                },
                                {
                                    field: 'variant',
                                    values: [WayfinderHotspotVariant.Kiosk],
                                },
                            ],
                        },
                        colorScheme: {
                            type: 'keyString',
                            displayName: 'Color Scheme',
                            tooltip: {
                                text: 'The color scheme of the hotspot. This will be used to style the hotspot. Use a name like "blue" or "green" instead of a hex code.',
                            },
                        },
                    },
                },
            },
        },
    },
};
//# sourceMappingURL=location_schema.js.map